import React, {
    useEffect,
    useState,
    useContext,
    useCallback,
    useRef,
    useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import firebase from "./firebase";
import dayjs from "dayjs";

export const AuthContext = React.createContext();

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null); // Firebase Auth User Object

    useEffect(() => {
        return firebase.auth().onAuthStateChanged(setCurrentUser);
    }, []);

    return (
        <AuthContext.Provider value={{ currentUser }}>
            {children}
        </AuthContext.Provider>
    );
};

export const UserContext = React.createContext();

export const UserProvider = ({ children }) => {
    const [currentUserData, setCurrentUserData] = useState(null); // User object from the database

    const { currentUser } = useContext(AuthContext);
    const { users } = useContext(DatabaseContext);

    // Personal data and permissions live on /userprofiles, which only the
    // owner and their admins can read. Nobody needs anyone else's, so this
    // reads exactly one record rather than the whole node.
    const [profile, setProfile] = useState(null);
    const [profileLoaded, setProfileLoaded] = useState(false);

    // The effect below re-runs on every users snapshot. so track which uid's
    // sign-in has been recorded to write lastLogin once per sign-in
    const lastLoginRecordedUidRef = useRef(null);

    useEffect(() => {
        if (!currentUser) {
            setProfile(null);
            setProfileLoaded(false);
            return;
        }

        setProfileLoaded(false);

        const ref = firebase
            .database()
            .ref("userprofiles")
            .child(currentUser.uid);

        const handleSnapshot = ref.on(
            "value",
            (snapshot) => {
                setProfile(snapshot.val() || {});
                setProfileLoaded(true);
            },
            (error) => {
                // Unblock rather than hang: a permanently failing profile read
                // leaves the app usable with no permissions, which fails
                // closed. Hanging here would take the whole site down.
                handleReadError("userprofiles for the current user")(error);
                setProfile(null);
                setProfileLoaded(true);
            },
        );

        return () => ref.off("value", handleSnapshot);
    }, [currentUser]);

    useEffect(() => {
        if (!currentUser) {
            lastLoginRecordedUidRef.current = null;
            setCurrentUserData(null);
            return;
        }

        const user = users.find((u) => u.uid === currentUser.uid);

        if (!user) {
            setCurrentUserData(null);
            return;
        }

        // Hold currentUserData back until the profile has been answered for.
        // Consumers treat a non-null currentUserData as proof that
        // permissions are known — several read currentUserData.permissions.x
        // behind nothing more than `if (currentUserData)` — and publishing
        // early would flash the wrong set of features at people.
        if (!profileLoaded) {
            // Sign-out clears this via the branch above, but a direct switch
            // from one account to another would otherwise leave the previous
            // person's details and permissions on screen while we wait.
            setCurrentUserData((current) =>
                current && current.uid !== currentUser.uid ? null : current,
            );
            return;
        }

        console.log("Setting Current User Data");
        setCurrentUserData({
            ...user,
            ...(profile || {}),
            // Never undefined, whatever the profile read did, so the
            // unguarded currentUserData.permissions.x reads cannot throw.
            permissions: (profile && profile.permissions) || {},
        });

        if (lastLoginRecordedUidRef.current !== currentUser.uid) {
            lastLoginRecordedUidRef.current = currentUser.uid;

            console.log("Updating Last Login");
            const ref = firebase.database().ref("userlogin").child(user.key);
            ref.update({
                lastLogin: firebase.database.ServerValue.TIMESTAMP,
            });
        }
    }, [currentUser, users, profile, profileLoaded]);

    return (
        <UserContext.Provider value={{ currentUserData }}>
            {children}
        </UserContext.Provider>
    );
};

// Cancel callback for ref.on() — without this, permission_denied silently
// detaches the listener and the UI just shows empty data
function handleReadError(nodeName) {
    return (error) =>
        console.error("Unable to get " + nodeName + " from Firebase", error);
}

// Attach a value listener at path, map each child through mapChild (return
// null to skip a record), and publish the resulting array via setState.
// Returns the detach function.
function subscribeToList(path, mapChild, setState, setLoaded) {
    console.log("Getting " + path + " from Firebase");
    const ref = firebase.database().ref(path);

    const handleSnapshot = ref.on(
        "value",
        function (snapshot) {
            let array = [];

            snapshot.forEach(function (childSnapshot) {
                const item = mapChild(childSnapshot.key, childSnapshot.val());
                if (item != null) {
                    array.push(item);
                }
            });

            setState(array);
            if (setLoaded) {
                setLoaded(true);
            }
        },
        (error) => {
            handleReadError(path)(error);
            if (setLoaded) {
                setLoaded(true);
            }
        },
    );

    return () => ref.off("value", handleSnapshot);
}

export const DatabaseContext = React.createContext();

export const DatabaseProvider = ({ children }) => {
    const { currentUser } = useContext(AuthContext);
    const location = useLocation();
    const isSharePage = location.pathname.startsWith("/share");
    const isSetPasswordPage = location.pathname.startsWith("/set-password");
    const shouldSkipDatabaseReads = isSharePage || isSetPasswordPage;

    const [users, setUsers] = useState([]);
    const [usersLoaded, setUsersLoaded] = useState(false);
    const [userLogins, setUserLogins] = useState([]);
    const [userConsent, setUserConsent] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [notificationsLoaded, setNotificationsLoaded] = useState(false);
    const [files, setFiles] = useState([]);
    const [filesLoaded, setFilesLoaded] = useState(false);
    const [folders, setFolders] = useState([]);
    const [foldersLoaded, setFoldersLoaded] = useState(false);
    const [comments, setComments] = useState([]);
    const [events, setEvents] = useState([]);
    const [eventsLoaded, setEventsLoaded] = useState(false);
    const [calendars, setCalendars] = useState([]);
    const [calendarsLoaded, setCalendarsLoaded] = useState(false);
    const [conversations, setConversations] = useState([]);
    const [conversationsLoaded, setConversationsLoaded] = useState(false);
    const [fileUsage, setFileUsage] = useState([]);
    const [groups, setGroups] = useState([]);
    const [groupsLoaded, setGroupsLoaded] = useState(false);
    const [playlistInfo, setPlaylistInfo] = useState([]);
    const [playlistInfoLoaded, setPlaylistInfoLoaded] = useState(false);
    const [playlistUsage, setPlaylistUsage] = useState([]);
    const [playlistUsageLoaded, setPlaylistUsageLoaded] = useState(false);
    const [timelineUsage, setTimelineUsage] = useState([]);
    const [timelineUsageLoaded, setTimelineUsageLoaded] = useState(false);
    const [timelineInfo, setTimelineInfo] = useState([]);
    const [timelineInfoLoaded, setTimelineInfoLoaded] = useState(false);
    const [surveyInfo, setSurveyInfo] = useState([]);
    const [surveyInfoLoaded, setSurveyInfoLoaded] = useState(false);
    const [projectInfo, setProjectInfo] = useState([]);
    const [projectInfoLoaded, setProjectInfoLoaded] = useState(false);

    // This is only used in ArchiveProgressDialog.jsx
    const [playlists, setPlaylists] = useState([]);

    // This is only used in ArchiveProgressDialog.jsx
    const [timelines, setTimelines] = useState([]);

    const isMountedRef = useRef(true);
    const fileUsageRequestRef = useRef(null);
    const fileUsageCacheRef = useRef(null);
    const playlistsRequestRef = useRef(null);
    const playlistsCacheRef = useRef(null);
    const timelinesRequestRef = useRef(null);
    const timelinesCacheRef = useRef(null);

    const getNotifications = useCallback((currentUser) => {
        if (!currentUser) return;

        return subscribeToList(
            "notifications",
            (key, data) => {
                const userIsTargeted =
                    (data.users && data.users[currentUser.uid]) ||
                    (data.managers && data.managers[currentUser.uid]);

                if (!userIsTargeted) return null;

                return {
                    key,
                    users: Object.keys(data.users || {}),
                    managers: Object.keys(data.managers || {}),
                    seenBy: Object.keys(data.seenBy || {}),
                    file: data.file,
                    type: data.type,
                    title: data.title,
                    message: data.message,
                    url: data.url,
                    thumbnail: data.thumbnail,
                    date: data.date,
                    fromDate: data.fromDate,
                    hubName: data.hubName,
                };
            },
            setNotifications,
            setNotificationsLoaded,
        );
    }, []);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        const cleanups = [];

        // Standalone public pages do not need any Realtime Database data
        if (shouldSkipDatabaseReads) {
            return;
        }

        cleanups.push(getUserConsent());

        if (currentUser) {
            setUsersLoaded(false);
            setNotificationsLoaded(false);
            setFilesLoaded(false);
            setFoldersLoaded(false);
            setEventsLoaded(false);
            setCalendarsLoaded(false);
            setGroupsLoaded(false);
            setPlaylistInfoLoaded(false);
            setPlaylistUsageLoaded(false);
            setTimelineInfoLoaded(false);
            setTimelineUsageLoaded(false);
            setSurveyInfoLoaded(false);
            setConversationsLoaded(false);
            cleanups.push(getUsers());
            cleanups.push(getUserLogins());
            cleanups.push(getNotifications(currentUser));
            cleanups.push(getFolders());
            cleanups.push(getGroups());
            cleanups.push(getFiles());
            cleanups.push(getComments());
            cleanups.push(getEvents());
            cleanups.push(getCalendars());
            cleanups.push(getConversations());
            cleanups.push(getPlaylistInfo());
            cleanups.push(getPlaylistUsage());
            cleanups.push(getTimelineInfo());
            cleanups.push(getTimelineUsage());
            cleanups.push(getSurveyInfo());
            cleanups.push(getProjectInfo());
        } else {
            resetAuthenticatedData();
        }

        return () => {
            cleanups.forEach((cleanup) => {
                if (typeof cleanup === "function") {
                    cleanup();
                }
            });
        };
    }, [currentUser, shouldSkipDatabaseReads, getNotifications]);

    function resetAuthenticatedData() {
        fileUsageRequestRef.current = null;
        fileUsageCacheRef.current = null;
        playlistsRequestRef.current = null;
        playlistsCacheRef.current = null;
        timelinesRequestRef.current = null;
        timelinesCacheRef.current = null;

        setUsers([]);
        setUsersLoaded(false);
        setUserLogins([]);
        setNotifications([]);
        setNotificationsLoaded(false);
        setFiles([]);
        setFilesLoaded(false);
        setFolders([]);
        setFoldersLoaded(false);
        setComments([]);
        setEvents([]);
        setEventsLoaded(false);
        setCalendars([]);
        setCalendarsLoaded(false);
        setConversations([]);
        setConversationsLoaded(false);
        setFileUsage([]);
        setGroups([]);
        setGroupsLoaded(false);
        setPlaylists([]);
        setPlaylistInfo([]);
        setPlaylistInfoLoaded(false);
        setPlaylistUsage([]);
        setPlaylistUsageLoaded(false);
        setTimelines([]);
        setTimelineInfo([]);
        setTimelineInfoLoaded(false);
        setTimelineUsage([]);
        setTimelineUsageLoaded(false);
        setSurveyInfo([]);
        setSurveyInfoLoaded(false);
        setProjectInfo([]);
        setProjectInfoLoaded(false);
    }

    function getUserConsent() {
        return subscribeToList(
            "userconsent",
            (key, data) => ({
                key: key,
                uid: data.uid,
                name: data.name,
                hasConsent: data.hasConsent,
            }),
            setUserConsent,
        );
    }

    // The directory every signed-in browser downloads: who exists, what they
    // are called, and what they can reach. Deliberately no personal data.
    //
    // email, phone, parentFirstName, parentLastName, parentEmail, parentPhone,
    // under18, allowEmailNotifications and permissions all live on
    // /userprofiles, which only the person themselves, their admins and system
    // managers can read. The current user's own copy is merged into
    // currentUserData by UserProvider; everyone else's is nobody's business
    // here, and the flows that used to need them now resolve them
    // server-side (sendUserNotification, checkEmailExists) or fetch the one
    // profile they are editing.
    function getUsers() {
        return subscribeToList(
            "users",
            (key, data) => ({
                key: key,
                uid: data.uid,
                firstName: data.firstName,
                lastName: data.lastName,
                fullName: data.firstName + " " + data.lastName,
                customRoleName: data.customRoleName,
                imageURL: data.imageURL,
                role: data.role,
                createdBy: data.createdBy,
                admins: Object.keys(data.admins || {}),
                files: Object.keys(data.files || {}),
                faveFiles: Object.keys(data.faveFiles || {}),
            }),
            setUsers,
            setUsersLoaded,
        );
    }

    function getUserLogins() {
        return subscribeToList(
            "userlogin",
            (key, data) => {
                // Server timestamps are numbers; older records hold display strings
                let lastLogin = data.lastLogin;
                if (typeof lastLogin === "number") {
                    lastLogin = dayjs(lastLogin).toString();
                }

                return {
                    key: key,
                    lastLogin: lastLogin,
                };
            },
            setUserLogins,
        );
    }

    function getFiles() {
        return subscribeToList(
            "files",
            (key, data) => ({
                key: key,
                filename: data.filename,
                displayName: data.displayName,
                folder: data.folder,
                type: data.type,
                allowDownloads: data.allowDownloads,
                allowComments: data.allowComments,
                allowSharing: data.allowSharing,
                url: data.url,
                thumbnail: data.thumbnail,
                original: data.original,
                uploadedBy: data.uploadedBy,
                events: Object.keys(data.events || {}).map((eventKey) => ({
                    key: eventKey,
                })),
                managers: Object.keys(data.managers || {}),
                groups: Object.keys(data.groups || {}),
                ancestors: Object.keys(data.ancestors || {}),
            }),
            setFiles,
            setFilesLoaded,
        );
    }

    function getFolders() {
        return subscribeToList(
            "folders",
            // If you change something here, also change in FileNavigatorPage useEffect, where clone folders are created
            (key, data) => ({
                key: key,
                id: key,
                name: data.name,
                hub: data.hub,
                parent: data.parent,
                imageURL: data.imageURL,
                createdBy: data.createdBy,
                parentNode: {},
                childNodes: [],
                managers: Object.keys(data.managers || {}),
                users: Object.keys(data.users || {}),
                ancestors: Object.keys(data.ancestors || {}),
            }),
            setFolders,
            setFoldersLoaded,
        );
    }

    function getGroups() {
        return subscribeToList(
            "groups",
            (key, data) => ({
                key: key,
                name: data.name,
                hub: data.hub,
                users: Object.keys(data.users || {}).map((userKey) => ({
                    key: userKey,
                })),
            }),
            setGroups,
            setGroupsLoaded,
        );
    }

    function getComments() {
        return subscribeToList(
            "comments",
            (key, data) => ({
                key: key,
                text: data.text,
                file: data.file,
                user: data.user,
                date: data.date,
                deleted: data.deleted,
                parent: data.parent,
                author: data.author,
            }),
            setComments,
        );
    }

    function getEvents() {
        return subscribeToList(
            "events",
            (key, data) => ({
                key: key,
                date: data.date,
                name: data.name,
                color: "#" + data.color,
                calendar: data.calendar,
                files: Object.keys(data.files || {}).map((fileKey) => ({
                    key: fileKey,
                })),
            }),
            setEvents,
            setEventsLoaded,
        );
    }

    const getFileUsage = useCallback(
        (refresh) => {
            const requestingUserUid = currentUser ? currentUser.uid : null;

            if (!requestingUserUid) {
                return Promise.resolve([]);
            }

            if (
                !refresh &&
                fileUsageCacheRef.current &&
                fileUsageCacheRef.current.uid === requestingUserUid
            ) {
                console.log("File Usage Already Downloaded");
                return Promise.resolve(fileUsageCacheRef.current.data);
            }

            if (
                fileUsageRequestRef.current &&
                fileUsageRequestRef.current.uid === requestingUserUid
            ) {
                return fileUsageRequestRef.current.promise;
            }

            console.log("Getting File Usage from Firebase");
            let ref = firebase.database().ref("fileusage");

            const snapshotRequest = ref.once("value").then(function (snapshot) {
                let array = [];

                snapshot.forEach(function (childSnapshot) {
                    let fileUsageKey = childSnapshot.key;
                    let fileUsageData = childSnapshot.val();

                    let usersSharedWith = [];

                    if (fileUsageData.usersSharedWith) {
                        Object.keys(fileUsageData.usersSharedWith).forEach(
                            (key) => {
                                usersSharedWith.push({
                                    key: key,
                                });
                            },
                        );
                    }

                    let groupsSharedWith = [];

                    if (fileUsageData.groupsSharedWith) {
                        Object.keys(fileUsageData.groupsSharedWith).forEach(
                            (key) => {
                                groupsSharedWith.push({
                                    key: key,
                                });
                            },
                        );
                    }

                    array.push({
                        key: fileUsageKey,
                        file: fileUsageData.file,
                        fileName: fileUsageData.fileName,
                        fileType: fileUsageData.fileType,
                        user: fileUsageData.user,
                        action: fileUsageData.action,
                        date: fileUsageData.date,
                        usersSharedWith: usersSharedWith,
                        groupsSharedWith: groupsSharedWith,
                    });
                });

                const activeUser = firebase.auth().currentUser;
                const requestIsStillActive =
                    isMountedRef.current &&
                    activeUser &&
                    activeUser.uid === requestingUserUid;

                if (requestIsStillActive) {
                    fileUsageCacheRef.current = {
                        uid: requestingUserUid,
                        data: array,
                    };
                    setFileUsage(array);
                }

                return array;
            });

            const request = snapshotRequest.then(
                (array) => {
                    if (
                        fileUsageRequestRef.current &&
                        fileUsageRequestRef.current.promise === request
                    ) {
                        fileUsageRequestRef.current = null;
                    }
                    return array;
                },
                (error) => {
                    if (
                        fileUsageRequestRef.current &&
                        fileUsageRequestRef.current.promise === request
                    ) {
                        fileUsageRequestRef.current = null;
                    }
                    console.error(
                        "Unable to get file usage from Firebase",
                        error,
                    );
                    return [];
                },
            );

            fileUsageRequestRef.current = {
                uid: requestingUserUid,
                promise: request,
            };

            return request;
        },
        [currentUser],
    );

    function getCalendars() {
        return subscribeToList(
            "calendars",
            (key, data) => ({
                key: key,
                name: data.name,
                hub: data.hub,
                user: data.user,
                events: Object.keys(data.events || {}).map((eventKey) => ({
                    key: eventKey,
                })),
            }),
            setCalendars,
            setCalendarsLoaded,
        );
    }

    function getConversations() {
        return subscribeToList(
            "conversations",
            (key, data) => ({
                key: key,
                participants: Object.keys(data.participants || {}).map(
                    (participantKey) => ({
                        key: participantKey,
                    }),
                ),
                messages: Object.entries(data.messages || {}).map(
                    ([messageKey, message]) => ({
                        key: messageKey,
                        date: message.date,
                        message: message.message,
                        deleted: message.deleted,
                        user: message.user,
                        fullName: message.fullName,
                        read: message.read,
                        readBy: Object.keys(message.readBy || {}),
                    }),
                ),
                lastMessage: data.lastMessage,
                title: data.title,
                deleted: data.deleted,
                imageURL: data.imageURL,
            }),
            setConversations,
            setConversationsLoaded,
        );
    }

    function getPlaylistInfo() {
        return subscribeToList(
            "playlistinfo",
            (key, data) => ({
                key: key,
                name: data.name,
                lastOpened: data.lastOpened,
                created: data.created,
                owner: data.owner,
                video: data.video,
                users: Object.keys(data.users || {}),
                groups: Object.keys(data.groups || {}),
            }),
            setPlaylistInfo,
            setPlaylistInfoLoaded,
        );
    }

    // This is only used in ArchiveProgressDialog.jsx
    const getPlaylists = useCallback(
        (refresh) => {
            const requestingUserUid = currentUser ? currentUser.uid : null;

            if (!requestingUserUid) {
                return Promise.resolve([]);
            }

            if (
                !refresh &&
                playlistsCacheRef.current &&
                playlistsCacheRef.current.uid === requestingUserUid
            ) {
                console.log("Playlists Already Downloaded");
                return Promise.resolve(playlistsCacheRef.current.data);
            }

            if (
                playlistsRequestRef.current &&
                playlistsRequestRef.current.uid === requestingUserUid
            ) {
                return playlistsRequestRef.current.promise;
            }

            console.log("Getting Playlists from Firebase");
            let ref = firebase.database().ref("playlists");

            const snapshotRequest = ref.once("value").then(function (snapshot) {
                let array = [];

                snapshot.forEach(function (childSnapshot) {
                    let key = childSnapshot.key;
                    let data = childSnapshot.val();

                    let clips = [];

                    if (data.clips) {
                        // Turn clips from a JSON object to an array

                        let clipKeys = Object.keys(data.clips);
                        let clipValues = Object.values(data.clips);

                        for (let i = 0; i < clipKeys.length; i++) {
                            let clipLabels = [];
                            if (clipValues[i].labels) {
                                let labelTextValues = Object.values(
                                    clipValues[i].labels,
                                );

                                for (
                                    let i = 0;
                                    i < labelTextValues.length;
                                    i++
                                ) {
                                    clipLabels.push(labelTextValues[i]);
                                }
                            }

                            clips.push({
                                key: clipKeys[i],
                                index: clipValues[i].index,
                                inTime: clipValues[i].inTime,
                                outTime: clipValues[i].outTime,
                                name: clipValues[i].name,
                                note: clipValues[i].note,
                                labels: clipLabels,
                                stars: clipValues[i].stars,
                                video: clipValues[i].video,
                            });
                        }
                    }

                    // Groups
                    let playlistGroups = [];
                    if (data.groups) {
                        let groupKeys = Object.keys(data.groups);

                        for (let i = 0; i < groupKeys.length; i++) {
                            playlistGroups.push(groupKeys[i]);
                        }
                    }

                    // Users
                    let playlistUsers = [];
                    if (data.users) {
                        let userKeys = Object.keys(data.users);

                        for (let i = 0; i < userKeys.length; i++) {
                            playlistUsers.push(userKeys[i]);
                        }
                    }

                    let playlist = {
                        key: key,
                        clips: clips,
                        name: data.name,
                        lastOpened: data.lastOpened,
                        created: data.created,
                        owner: data.owner,
                        video: data.video,
                        users: playlistUsers,
                        groups: playlistGroups,
                    };

                    array.push(playlist);
                });

                const activeUser = firebase.auth().currentUser;
                const requestIsStillActive =
                    isMountedRef.current &&
                    activeUser &&
                    activeUser.uid === requestingUserUid;

                if (requestIsStillActive) {
                    playlistsCacheRef.current = {
                        uid: requestingUserUid,
                        data: array,
                    };
                    setPlaylists(array);
                }

                return array;
            });

            const request = snapshotRequest.then(
                (array) => {
                    if (
                        playlistsRequestRef.current &&
                        playlistsRequestRef.current.promise === request
                    ) {
                        playlistsRequestRef.current = null;
                    }
                    return array;
                },
                (error) => {
                    if (
                        playlistsRequestRef.current &&
                        playlistsRequestRef.current.promise === request
                    ) {
                        playlistsRequestRef.current = null;
                    }
                    console.error(
                        "Unable to get playlists from Firebase",
                        error,
                    );
                    return [];
                },
            );

            playlistsRequestRef.current = {
                uid: requestingUserUid,
                promise: request,
            };

            return request;
        },
        [currentUser],
    );

    function getPlaylistUsage() {
        return subscribeToList(
            "playlistusage",
            (key, data) => ({
                key: key,
                playlist: data.playlist,
                user: data.user,
                lastOpened: data.lastOpened,
            }),
            setPlaylistUsage,
            setPlaylistUsageLoaded,
        );
    }

    // The per person record of opening a timeline. timelineinfo.lastOpened is
    // a single field written by whoever opened it, so it cannot answer "when
    // did THIS user last open this", which is what Recently opened asks.
    function getTimelineUsage() {
        return subscribeToList(
            "timelineusage",
            (key, data) => ({
                key: key,
                timeline: data.timeline,
                user: data.user,
                lastOpened: data.lastOpened,
            }),
            setTimelineUsage,
            setTimelineUsageLoaded,
        );
    }

    function getSurveyInfo() {
        return subscribeToList(
            "surveyinfo",
            (key, data) => ({
                key: key,
                title: data.title,
                intro: data.intro,
                createdBy: data.createdBy,
                createdOn: data.createdOn,
                lastUpdated: data.lastUpdated,
                file: data.file,
                anonymous: data.anonymous,
                groups: Object.entries(data.groups || {}).map(
                    ([groupKey, group]) => ({
                        key: groupKey,
                        canEdit: group.canEdit,
                        canViewResults: group.canViewResults,
                    }),
                ),
                users: Object.entries(data.users || {}).map(
                    ([userKey, user]) => ({
                        key: userKey,
                        canEdit: user.canEdit,
                        canViewResults: user.canViewResults,
                    }),
                ),
            }),
            setSurveyInfo,
            setSurveyInfoLoaded,
        );
    }

    function getProjectInfo() {
        return subscribeToList(
            "projectinfo",
            (key, data) => ({
                key: key,
                title: data.title,
                intro: data.intro,
                createdBy: data.createdBy,
                createdOn: data.createdOn,
                lastUpdated: data.lastUpdated,
                groups: Object.entries(data.groups || {}).map(
                    ([groupKey, group]) => ({
                        key: groupKey,
                        canEdit: group.canEdit,
                    }),
                ),
                users: Object.entries(data.users || {}).map(
                    ([userKey, user]) => ({
                        key: userKey,
                        canEdit: user.canEdit,
                    }),
                ),
            }),
            setProjectInfo,
            setProjectInfoLoaded,
        );
    }

    function getTimelineInfo() {
        return subscribeToList(
            "timelineinfo",
            (key, data) => ({
                key: key,
                name: data.name,
                owner: data.owner,
                lastOpened: data.lastOpened,
                created: data.created,
                video: data.video,
                users: Object.keys(data.users || {}),
                groups: Object.keys(data.groups || {}),
            }),
            setTimelineInfo,
            setTimelineInfoLoaded,
        );
    }

    // This is only used in ArchiveProgressDialog.jsx
    const getTimelines = useCallback(
        (refresh) => {
            const requestingUserUid = currentUser ? currentUser.uid : null;

            if (!requestingUserUid) {
                return Promise.resolve([]);
            }

            if (
                !refresh &&
                timelinesCacheRef.current &&
                timelinesCacheRef.current.uid === requestingUserUid
            ) {
                console.log("Timelines Already Downloaded");
                return Promise.resolve(timelinesCacheRef.current.data);
            }

            if (
                timelinesRequestRef.current &&
                timelinesRequestRef.current.uid === requestingUserUid
            ) {
                return timelinesRequestRef.current.promise;
            }

            console.log("Getting Timelines from Firebase");

            let ref = firebase.database().ref("timelines");
            const snapshotRequest = ref.once("value").then(function (snapshot) {
                let array = [];

                snapshot.forEach(function (childSnapshot) {
                    let key = childSnapshot.key;
                    let data = childSnapshot.val();

                    let instances = [];

                    if (data.instances) {
                        // Turn instances from a JSON object to an array

                        let clipKeys = Object.keys(data.instances);
                        let clipValues = Object.values(data.instances);

                        for (let i = 0; i < clipKeys.length; i++) {
                            // Labels
                            let instanceLabels = [];
                            if (clipValues[i].labels) {
                                let labelTextValues = Object.values(
                                    clipValues[i].labels,
                                );

                                for (
                                    let i = 0;
                                    i < labelTextValues.length;
                                    i++
                                ) {
                                    instanceLabels.push(labelTextValues[i]);
                                }
                            }

                            // Notes
                            let instanceNotes = [];
                            if (clipValues[i].notes) {
                                let noteTextValues = Object.values(
                                    clipValues[i].notes,
                                );

                                for (
                                    let i = 0;
                                    i < noteTextValues.length;
                                    i++
                                ) {
                                    instanceNotes.push(noteTextValues[i]);
                                }
                            }

                            instances.push({
                                key: clipKeys[i],
                                index: clipValues[i].index,
                                name: clipValues[i].name,
                                labels: instanceLabels,
                                notes: instanceNotes,
                                note: clipValues[i].note,
                                stars: clipValues[i].stars,
                                inTime: clipValues[i].inTime,
                                outTime: clipValues[i].outTime,
                            });
                        }
                    }

                    // Groups
                    let timelineGroups = [];
                    if (data.groups) {
                        let groupKeys = Object.keys(data.groups);

                        for (let i = 0; i < groupKeys.length; i++) {
                            timelineGroups.push(groupKeys[i]);
                        }
                    }

                    // Users
                    let timelineUsers = [];
                    if (data.users) {
                        let userKeys = Object.keys(data.users);

                        for (let i = 0; i < userKeys.length; i++) {
                            timelineUsers.push(userKeys[i]);
                        }
                    }

                    let timeline = {
                        key: key,
                        instances: instances,
                        name: data.name,
                        owner: data.owner,
                        lastOpened: data.lastOpened,
                        created: data.created,
                        video: data.video,
                        users: timelineUsers,
                        groups: timelineGroups,
                    };

                    array.push(timeline);
                });

                const activeUser = firebase.auth().currentUser;
                const requestIsStillActive =
                    isMountedRef.current &&
                    activeUser &&
                    activeUser.uid === requestingUserUid;

                if (requestIsStillActive) {
                    timelinesCacheRef.current = {
                        uid: requestingUserUid,
                        data: array,
                    };
                    setTimelines(array);
                }

                return array;
            });

            const request = snapshotRequest.then(
                (array) => {
                    if (
                        timelinesRequestRef.current &&
                        timelinesRequestRef.current.promise === request
                    ) {
                        timelinesRequestRef.current = null;
                    }
                    return array;
                },
                (error) => {
                    if (
                        timelinesRequestRef.current &&
                        timelinesRequestRef.current.promise === request
                    ) {
                        timelinesRequestRef.current = null;
                    }
                    console.error(
                        "Unable to get timelines from Firebase",
                        error,
                    );
                    return [];
                },
            );

            timelinesRequestRef.current = {
                uid: requestingUserUid,
                promise: request,
            };

            return request;
        },
        [currentUser],
    );

    // Memoized so provider re-renders that change no slice (e.g. route
    // changes via useLocation) don't re-render every consumer
    const value = useMemo(
        () => ({
            userConsent,
            users,
            usersLoaded,
            userLogins,
            notifications,
            notificationsLoaded,
            files,
            filesLoaded,
            folders,
            foldersLoaded,
            comments,
            events,
            eventsLoaded,
            calendars,
            calendarsLoaded,
            conversations,
            conversationsLoaded,
            fileUsage,
            getFileUsage,
            groups,
            groupsLoaded,
            playlists,
            playlistInfo,
            playlistInfoLoaded,
            playlistUsage,
            playlistUsageLoaded,
            getPlaylists,
            timelines,
            timelineInfo,
            timelineInfoLoaded,
            timelineUsage,
            timelineUsageLoaded,
            getTimelines,
            surveyInfo,
            surveyInfoLoaded,
            projectInfo,
            projectInfoLoaded,
        }),
        [
            userConsent,
            users,
            usersLoaded,
            userLogins,
            notifications,
            notificationsLoaded,
            files,
            filesLoaded,
            folders,
            foldersLoaded,
            comments,
            events,
            eventsLoaded,
            calendars,
            calendarsLoaded,
            conversations,
            conversationsLoaded,
            fileUsage,
            getFileUsage,
            groups,
            groupsLoaded,
            playlists,
            playlistInfo,
            playlistInfoLoaded,
            playlistUsage,
            playlistUsageLoaded,
            getPlaylists,
            timelines,
            timelineInfo,
            timelineInfoLoaded,
            timelineUsage,
            timelineUsageLoaded,
            getTimelines,
            surveyInfo,
            surveyInfoLoaded,
            projectInfo,
            projectInfoLoaded,
        ],
    );

    return (
        <DatabaseContext.Provider value={value}>
            {children}
        </DatabaseContext.Provider>
    );
};
